// Le toast du produit : la confirmation sombre en bas à gauche.
//
// Chaque écran portait le sien : son état, son minuteur, et le même bloc de
// rendu recopié onze fois. Maintenant que la coquille est montée au-dessus
// des écrans, elle peut le tenir pour tout le monde : un écran dit ce qu'il
// veut confirmer, la coquille l'affiche et l'efface. C'est le geste de Dust,
// dont n'importe quel composant appelle `sendNotification` sans se demander
// où la notification sera rendue.
//
// Deux contextes plutôt qu'un, comme pour la configuration de la coquille :
// l'écran qui déclenche une confirmation ne lit pas celle qui est affichée,
// donc il ne se rend pas à nouveau quand elle change.

import type { ReactNode } from "react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

// Le temps qu'une confirmation reste à l'écran.
export const DUREE_TOAST_MS = 4000;

export interface ToastCompta {
  titre: string;
  sousTitre?: string;
  // Le bouton de droite (Ouvrir, Annuler, Voir). Le toast se referme quand
  // on l'active : c'est toujours ce que l'écran faisait à la main.
  action?: string;
  onAction?: () => void;
}

interface ToastAffiche {
  toast: ToastCompta | null;
  masquer: () => void;
}

const AfficheContexte = createContext<ToastAffiche | null>(null);
const MontrerContexte = createContext<((toast: ToastCompta) => void) | null>(
  null
);

interface FournisseurToastProps {
  children: ReactNode;
  duree?: number;
}

export function FournisseurToast({
  children,
  duree = DUREE_TOAST_MS,
}: FournisseurToastProps) {
  const [courant, setCourant] = useState<ToastCompta | null>(null);
  const minuteur = useRef<number | undefined>(undefined);

  const masquer = useCallback(() => {
    window.clearTimeout(minuteur.current);
    setCourant(null);
  }, []);

  const montrer = useCallback(
    (toast: ToastCompta) => {
      window.clearTimeout(minuteur.current);
      setCourant(toast);
      minuteur.current = window.setTimeout(() => setCourant(null), duree);
    },
    [duree]
  );

  // La coquille vit tant que l'application vit, mais le minuteur ne doit pas
  // survivre à un démontage en test ou en développement.
  useEffect(() => () => window.clearTimeout(minuteur.current), []);

  const affiche = useMemo(
    () => ({ toast: courant, masquer }),
    [courant, masquer]
  );

  return (
    <MontrerContexte.Provider value={montrer}>
      <AfficheContexte.Provider value={affiche}>
        {children}
      </AfficheContexte.Provider>
    </MontrerContexte.Provider>
  );
}

/** Appelé par un écran pour confirmer ce qu'il vient de faire. */
export function useMontrerToast(): (toast: ToastCompta) => void {
  const montrer = useContext(MontrerContexte);
  if (!montrer) {
    throw new Error(
      "useMontrerToast doit être appelé sous un FournisseurToast"
    );
  }
  return montrer;
}

/** Lu par la coquille seule, qui est la seule à le rendre. */
export function useToastAffiche(): ToastAffiche {
  return useContext(AfficheContexte) ?? { toast: null, masquer: () => {} };
}
